import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Combines class names with tailwind-merge for proper Tailwind CSS class handling
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format price in Russian rubles - оптимизированная версия без Intl.NumberFormat
 * Быстрое форматирование для лучшего INP
 */
export function formatPrice(price: number): string {
  const rounded = Math.round(price);
  return `${rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} ₽`;
}

/**
 * Вес блюда для показа. Решение владельца (вопрос 31, вариант А):
 * в базе лежит ЧИСЛО, единицу дописывает интерфейс.
 *
 * Пока база не почищена, часть весов записана строкой («250 г», «250гр»), поэтому
 * из значения вытаскивается число, а «г» ставится ровно один раз — иначе в карточке
 * получалось бы «250 г г». Когда данные приведут к числам, поведение не изменится.
 * Не число вовсе («по запросу») отдаём как есть, ничего не дописывая.
 */
export function formatWeight(weight?: number | string | null): string | null {
  if (weight === null || weight === undefined) return null;

  if (typeof weight === 'number') {
    return Number.isFinite(weight) && weight > 0 ? `${weight} г` : null;
  }

  const raw = String(weight).trim();
  if (!raw) return null;

  // «250», «250 г», «250гр», «250,5 г» → 250 / 250.5
  const match = raw.replace(',', '.').match(/^\s*(\d+(?:\.\d+)?)\s*(?:г|гр|g)?\s*$/i);
  if (!match) return raw;

  const value = Number(match[1]);
  return Number.isFinite(value) && value > 0 ? `${value} г` : null;
}

/**
 * Format phone number to Russian format
 */
export function formatPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11) {
    return `+7 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7, 9)}-${cleaned.slice(9)}`;
  }
  if (cleaned.length === 10) {
    return `+7 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 8)}-${cleaned.slice(8)}`;
  }
  return phone;
}

/**
 * Format date in Russian locale
 */
export function formatDate(date: string | Date): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(date));
}

/**
 * Format relative time (e.g., "5 минут назад")
 */
export function formatRelativeTime(date: string | Date): string {
  const now = new Date();
  const then = new Date(date);
  const diffInSeconds = Math.floor((now.getTime() - then.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'только что';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} ${pluralize(diffInMinutes, 'минуту', 'минуты', 'минут')} назад`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} ${pluralize(diffInHours, 'час', 'часа', 'часов')} назад`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} ${pluralize(diffInDays, 'день', 'дня', 'дней')} назад`;
  }

  return formatDate(date);
}

/**
 * Russian pluralization helper
 */
export function pluralize(
  count: number,
  one: string,
  few: string,
  many: string
): string {
  const mod10 = count % 10;
  const mod100 = count % 100;

  if (mod100 >= 11 && mod100 <= 19) {
    return many;
  }

  if (mod10 === 1) {
    return one;
  }

  if (mod10 >= 2 && mod10 <= 4) {
    return few;
  }

  return many;
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, length: number): string {
  if (text.length <= length) return text;
  return text.slice(0, length).trim() + '...';
}

/**
 * Generate a random ID
 */
export function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

/**
 * Delay execution
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Check if code is running on server
 */
export const isServer = typeof window === 'undefined';

/**
 * Check if code is running on client
 */
export const isClient = !isServer;
